/* eslint-disable prettier/prettier */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  Input,
  ChangeDetectorRef,
  Directive,
  OnInit,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  AfterViewInit,
  Inject,
  Optional,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { DOCUMENT } from '@angular/common';

@Directive()
export abstract class BaseAccordionComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @Input() expandState?: string;
  @Input() isSectionBg?: boolean;
  @Input() headerLabel: string = 'Accordion';
  @Input() title: string | undefined = '';
  @Input() storybook: boolean = false;

  isOpen: boolean[] = [];
  fragmentSubscription?: Subscription;

  constructor(
    protected cdr: ChangeDetectorRef,
    @Optional() protected route?: ActivatedRoute,
    @Optional() @Inject(DOCUMENT) protected document?: Document,
    @Optional() protected router?: Router
  ) {}

  ngOnInit() {
    this.initOpenStates();
  }

  ngAfterViewInit() {
    // Handle URL fragment if route is available
    if (this.route) {
      this.handleFragment();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['expandState']) {
      this.setOpenState();
    }

    if (changes['caasData'] && this.hasCaasData()) {
      this.processCaasData();
    }
  }

  ngOnDestroy(): void {
    if (this.fragmentSubscription) {
      this.fragmentSubscription.unsubscribe();
    }
  }

  /**
   * Abstract method to return items array
   * All child components must implement this method
   */
  protected abstract getItems(): any[];

  /**
   * Check if this component has CaaS data
   * Child components can override this if needed
   */
  protected hasCaasData(): boolean {
    return false;
  }

  /**
   * Process CaaS data when it changes
   * Child components should override this method
   */
  protected processCaasData(): void {
    // Default implementation does nothing
    // Child components should override this
  }

  /**
   * Initialize open states for all items
   */
  protected initOpenStates() {
    this.isOpen = this.getItems().map(() => false);
  }

  /**
   * Set open state based on expandState property
   */
  protected setOpenState() {
    const itemCount = this.getItems().length;
    switch (this.expandState) {
      case 'firstOpen':
        this.isOpen = Array(itemCount).fill(false);
        this.isOpen[0] = true;
        break;
      case 'allOpen':
        this.isOpen = Array(itemCount).fill(true);
        break;
      case 'allClosed':
      default:
        this.isOpen = Array(itemCount).fill(false);
        break;
    }

    this.cdr.detectChanges();
  }

  /**
   * Toggle open/close state of an item
   */
  toggleOpenClose(index: number) {
    if (this.isOpen[index]) {
      this.isOpen[index] = false;
    } else {
      this.isOpen.fill(false);
      this.isOpen[index] = true;
    }

    this.cdr.detectChanges();
  }

  /**
   * Set open state based on CaaS data
   */
  setCaasOpenState(items: any[], expandState: string | undefined) {
    const length = items?.length ?? 0;
    this.isOpen = new Array(length).fill(false);

    switch (expandState) {
      case 'firstOpen':
        if (length > 0) {
          this.isOpen[0] = true;
        }
        break;
      case 'allOpen':
        this.isOpen.fill(true);
        break;
      case 'allClosed':
      default:
        this.isOpen.fill(false);
        break;
    }
    this.cdr.detectChanges();
  }

  /**
   * Handle URL fragment to open specific accordion item
   */
  protected handleFragment(): void {
    if (!this.route) return;

    this.fragmentSubscription = this.route.fragment.subscribe((fragment) => {
      if (fragment) {
        this.openAccordionById(fragment);
      }
    });
  }
  /**
   * Handle keyboard events for accessibility
   * Allows toggling accordion items with Enter or Space keys
   */
  handleEventDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const target = event.target as HTMLElement;
      const index = Array.from(target.parentElement!.children).indexOf(target);
      this.toggleOpenClose(index);
    }
  }
  /**
   * Open accordion item by ID
   * Child classes can override this for custom ID matching logic
   */
  protected openAccordionById(id: string): void {
    const items = this.getItems();
    const index = items.findIndex(
      (item) =>
        this.getItemId(item)?.toString().trim().toLowerCase() ===
        id.toString().trim().toLowerCase()
    );

    if (index !== -1) {
      this.isOpen.fill(false);
      this.isOpen[index] = true;

      // Optional callback when item is opened
      this.onItemOpened(index, id);

      // Scroll to the accordion after it's opened
      this.scrollToElement(id);
    }
  }

  /**
   * Get the ID from an item
   * Child classes should override this for custom ID extraction
   */
  protected getItemId(item: any): string | number | undefined {
    return item.id || item.key;
  }

  /**
   * Optional callback when an item is opened
   * Child classes can override this for custom behavior
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected onItemOpened(_: number, __: string): void {
    // Default implementation does nothing
    // Child components can override this
  }

  /**
   * Scroll to element by ID
   */
  protected scrollToElement(id: string, offset: number = 100): void {
    if (!this.document) return;

    setTimeout(() => {
      const element = this.document!.getElementById(id);
      if (element) {
        if (typeof window !== 'undefined') {
          const topOffset =
            element.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top: topOffset, behavior: 'smooth' });
        }
      }
    }, 100);
  }
}
